import { ProjectPricingBreakdown } from "../Pricing/PricingBreakdown";
import { OrderPricingBreakdownData } from "../Pricing/OrderPricingBreakdown";

export type ProposalTier = "good" | "better" | "best";

export const TIER_ORDER: ProposalTier[] = ["good", "better", "best"];

export interface WizardContentType {
    id: number;
    name: string;
    display_name: string;
    // False for brand-level content (Founder Story) — no product attaches.
    requires_product?: boolean;
    rank: number;
    tier: ProposalTier;
    reason?: string | null;
    // Matrix 1 unit counts (objective as PRIMARY goal)…
    qty_good: number;
    qty_better: number;
    qty_best: number;
    // …and Matrix 2 overlay counts (objective as SECONDARY goal). The final
    // package takes max(primary qty, secondary overlay qty) per content
    // type; good/better/best packages use the good/good/better overlays.
    overlay_qty_good: number;
    overlay_qty_better: number;
}

export interface WizardChannelSuggestion {
    id: number;
    name: string;
    rank: number;
}

export interface WizardObjective {
    id: number;
    name: string;
    slug: string;
    description?: string | null;
    examples?: string | null;
    icon?: string | null;
    order: number;
    content_types: WizardContentType[];
    channels: WizardChannelSuggestion[];
}

export interface WizardChannel {
    id: number;
    name: string;
}

export interface WizardProductCategory {
    id: number;
    name: string;
    slug?: string | null;
    // Null for L1 roots; L2 categories carry their L1 parent's id and are
    // grouped under it in the wizard's category select.
    parent_id?: number | null;
}

// Crawled retailer category (RMN config endpoint only). Shown as a first
// "retailer category" dropdown in the RMN wizard; product_category_id is
// the crawl-time Gemini mapping that pre-selects the TDC category dropdown.
// A picker aid only — never persisted on the proposal.
export interface RmnWizardCategory {
    id: number;
    name: string;
    parent_id?: number | null;
    product_category_id?: number | null;
}

// ---------------------------------------------------------------------------
// GET /public/proposals/examples — demo videos + top Expert IQ experts for
// the selected product category.
// ---------------------------------------------------------------------------

export interface ProposalExampleVideo {
    id: number;
    title: string;
    location: string;
    thumbnail: string | null;
    length?: number | null;
    aspect_ratio?: string | null;
    content_type: { id: number; name: string; display_name: string } | null;
    brand_name: string | null;
    product_title: string | null;
}

export interface ProposalExampleExpert {
    userid: number;
    display_name: string;
    avatar: string;
    credential: string | null;
    blurb: string | null;
    industry_years: string | null;
    score: number;
    // Server-composed "why this expert" line (profession, years, Expert IQ
    // affinity band, top product match).
    relevance: string;
}

// Internal-only coverage grid: the resolved example for one
// (content type × channel) cell, with where it came from.
export interface ProposalExampleSlot {
    content_type_id: number;
    content_channel_id: number;
    source: "channel-default" | "category-default" | "fallback" | null;
    video: ProposalExampleVideo | null;
}

export interface ProposalExamplesResponse {
    product_category_id: number;
    videos: ProposalExampleVideo[];
    // Present only for internal viewers when content_type_ids + channel_ids
    // were supplied.
    slots?: ProposalExampleSlot[];
    experts: ProposalExampleExpert[];
}

export interface ProposalWizardConfig {
    tiers: ProposalTier[];
    objectives: WizardObjective[];
    channels: WizardChannel[];
    product_categories: WizardProductCategory[];
    // RMN config endpoint only.
    rmn_categories?: RmnWizardCategory[];
    rmn?: { brand_id: number; name: string };
}

export interface ProposalProductInput {
    desireid?: number;
    title?: string;
    url?: string;
    image?: string;
    brand_name?: string;
}

// A product picked in the strategy wizard's Step 1 (TDC-1499). "desire" =
// imported catalog product (keyed desireid); "discovered" = crawl staging
// row (keyed id) that the server auto-imports when the strategy is created.
export interface WizardProductRef {
    kind: "desire" | "discovered";
    desireid?: number;
    id?: number;
    title: string;
    image?: string;
    url?: string;
}

// What POST /rmn/proposals expects per entry in body.products.
export interface ProposalProductLinkInput {
    desireid?: number;
    discovered_product_id?: number;
}

// One selected channel INSTANCE — duplicates in the array = quantity,
// mirroring ChannelQuantityPicker / brand_projects_channels semantics.
export interface SelectedChannel {
    id: number;
    name: string;
}

// PDP channel sub-type — a wizard-only refinement of the PDP channel (not a
// channel itself), stamped into proposal_meta server-side for analytics.
export type PdpSubtype = "retail" | "d2c" | "amazon";
export const PDP_SUBTYPES: PdpSubtype[] = ["retail", "d2c", "amazon"];
export const PDP_SUBTYPE_LABELS: Record<PdpSubtype, string> = {
    retail: "Retail",
    d2c: "D2C",
    amazon: "Amazon",
};

export interface ContentTypeUnitCount {
    content_type_id: number;
    quantity: number;
}

export interface PerContentTypeEstimate {
    content_type_id: number;
    quantity: number;
    unit_total: number;
    subtotal: number;
    breakdown: ProjectPricingBreakdown;
}

export interface TierEstimate {
    // null (and savingsPct 0) for anonymous callers — the API withholds
    // dollar figures from unauthenticated sessions (TDC-1563).
    total: number | null;
    projectCount: number;
    savingsPct: number;
    content_type_ids: number[];
    content_type_units: ContentTypeUnitCount[];
    // Present only for internal viewers.
    order?: { total: number; breakdown: OrderPricingBreakdownData };
    per_project_estimates?: Array<{
        key: number | string;
        total: number;
        breakdown: ProjectPricingBreakdown;
    }>;
    per_content_type_estimates?: PerContentTypeEstimate[];
}

export interface ProposalPricingPreviewResponse {
    objective_id: number;
    secondary_objective_id?: number | null;
    tiers: Partial<Record<ProposalTier, TierEstimate>>;
}

export interface CreatedProposal {
    id: number;
    name: string;
    status: string;
    tier: ProposalTier;
    objective_id: number;
    secondary_objective_id?: number | null;
    product_category_id: number;
    // null for anonymous creators (TDC-1563).
    pricing_total: string | number | null;
    // The order's public_page_token — backs /proposal/view/:token.
    share_token?: string | null;
    // Linked catalog products (TDC-1499) — discovered picks come back as
    // desires (the server auto-imports on link).
    products?: Array<{ desireid: number; title: string }>;
    projects: Array<{ id: number; title: string; content_type_id: number }>;
}

// ---------------------------------------------------------------------------
// Detail responses (GET /admin/sales/proposals/:id, GET /public/proposals/:token)
// ---------------------------------------------------------------------------

export interface ProposalObjectiveDetail {
    id: number;
    name: string;
    slug: string;
    description?: string | null;
    examples?: string | null;
    icon?: string | null;
    content_types: WizardContentType[];
}

export interface ProposalChannelWithQuantity {
    id: number;
    name: string;
    quantity: number;
}

export interface ProposalContentTypeUnit {
    content_type_id: number;
    name: string | null;
    display_name: string | null;
    quantity: number;
}

export interface ProposalDetailProject {
    id: number;
    title: string;
    status: string;
    content_type: {
        id: number;
        name: string;
        display_name: string;
        requires_product?: boolean;
    } | null;
    // Internal viewers only.
    pricing_total?: string | number | null;
    pricing_breakdown?: ProjectPricingBreakdown | null;
}

export interface ProposalDetail {
    id: number;
    name: string;
    status: string;
    tier: ProposalTier | null;
    created_at: string;
    updated_at: string;
    // Drawn from the analysis the strategy was created from; null when it
    // started blank.
    description?: string | null;
    proposal_meta: {
        product_title?: string | null;
        product_url?: string | null;
        product_image?: string | null;
        brand_name?: string | null;
        pdp_subtype?: PdpSubtype | null;
    } | null;
    // Convenience mirror of proposal_meta.pdp_subtype (null when the PDP
    // channel isn't part of the proposal).
    pdp_subtype?: PdpSubtype | null;
    lead: { id: number; email: string } | null;
    // rmn (TDC-1591): the proposal belongs to an RMN deal — the portal hides
    // all pricing on it for non-internal viewers.
    brand: { id: number; name: string; logo?: string | null; rmn?: boolean } | null;
    objective: ProposalObjectiveDetail | null;
    secondary_objective: ProposalObjectiveDetail | null;
    product_category: { id: number; name: string; slug?: string | null } | null;
    // Flat instance array (duplicates = quantity) for edit round-trips…
    channel_ids: number[];
    // …and the aggregated display shape.
    channels: ProposalChannelWithQuantity[];
    content_type_units: ProposalContentTypeUnit[];
    pricing: {
        // All three are nulled/zeroed for anonymous viewers (TDC-1563).
        total: string | number | null;
        savings_pct: number;
        snapshotted_at: string | null;
        // Internal viewers only.
        breakdown?: OrderPricingBreakdownData | null;
    };
    projects: ProposalDetailProject[];
    // Linked catalog products (TDC-1499).
    products?: Array<{ desireid: number; title: string; image?: string | null }>;
    // The PDP audit this proposal was created from, if any.
    pdp_audit_report?: { id: number; url: string; created_at: string } | null;
    // Curated shortlist of additional example videos.
    video_shortlist?: {
        id: number;
        name: string;
        videos: ProposalExampleVideo[];
    } | null;
    // Internal viewers only.
    created_by?: number | null;
    share_token?: string | null;
}
